import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { type Project } from '@/lib/supabase';
import { MapPin, ArrowUpRight } from 'lucide-react';

interface ProjectCardProps {
  project: Project;
}

const ProjectCard: React.FC<ProjectCardProps> = ({ project }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/projecten/${project.id}`);
  };

  const getStatusText = (status?: string) => {
    switch (status) {
      case 'planned':
        return 'Gepland';
      case 'in_progress':
        return 'In uitvoering';
      case 'completed':
      default:
        return 'Opgeleverd';
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'planned':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'in_progress':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'completed':
      default:
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    }
  };

  return (
    <Card 
      className="group h-full bg-card border-border/50 shadow-card hover:shadow-card-hover transition-all duration-500 hover:-translate-y-2 overflow-hidden cursor-pointer"
      onClick={handleClick}
    >
      <div className="relative overflow-hidden">
        <div className="aspect-[16/10] bg-muted overflow-hidden">
          {project.photo || project.main_photo_url ? (
            <img 
              src={(project.photo || project.main_photo_url)?.startsWith('http')
                ? (project.photo || project.main_photo_url)!
                : (project.photo || project.main_photo_url)!.startsWith('src/')
                  ? `/${project.photo || project.main_photo_url}`
                  : `https://mgpylqzfxfflpucywzwh.supabase.co/storage/v1/object/public/project-images/${project.photo || project.main_photo_url}`}
              alt={project.title}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-primary/15 to-primary/5 flex items-center justify-center">
              <span className="text-muted-foreground font-medium">Project foto</span>
            </div>
          )}
        </div>
        
        {/* Gradient overlay on hover */}
        <div className="absolute inset-0 bg-gradient-to-t from-primary/60 via-primary/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        
        {/* Status badge */}
        <div className="absolute top-4 left-4">
          <Badge className={`${getStatusColor(project.status)} border font-medium shadow-sm`}>
            {getStatusText(project.status)}
          </Badge>
        </div>
        
        {/* Hover icon */}
        <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-2 group-hover:translate-y-0">
          <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-card">
            <ArrowUpRight className="h-5 w-5 text-primary" />
          </div>
        </div>
        
        {/* Hover title overlay */}
        <div className="absolute bottom-4 left-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-4 group-hover:translate-y-0">
          <span className="text-white/90 text-sm font-medium">Bekijk project →</span>
        </div>
      </div>

      <CardHeader className="pb-3">
        <h3 className="font-serif text-xl font-medium text-primary group-hover:text-primary/80 transition-colors duration-300">
          {project.title}
        </h3>
        <div className="flex items-center text-muted-foreground">
          <MapPin className="h-4 w-4 mr-2 text-accent" />
          <span className="text-sm">{project.city}</span>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground line-clamp-3 leading-relaxed">
          {project.description || 'Meer informatie over dit project is beschikbaar op aanvraag.'}
        </p>
        
        {project.highlights && project.highlights.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {project.highlights.slice(0, 3).map((highlight, index) => (
              <Badge key={index} variant="secondary" className="bg-secondary/80 text-foreground/80 text-xs font-medium">
                {highlight}
              </Badge>
            ))}
            {project.highlights.length > 3 && (
              <Badge variant="secondary" className="text-xs">
                +{project.highlights.length - 3}
              </Badge>
            )}
          </div>
        )}
        
        <div className="pt-2">
          <span className="inline-flex items-center text-sm font-medium text-primary group-hover:text-accent transition-colors duration-300">
            Bekijk project details
            <ArrowUpRight className="h-4 w-4 ml-1 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProjectCard;